
import { mapActions } from "vuex";

import { getVariantTitle } from "~/utils/shopify-utils";
import { saleTagMixin, tagableMixin } from "~/mixins";
import { slugify, camelize } from "~/utils";

const QUICKVIEW_VERTICAL_OFFSET = 400;

export default {
  name: "BundleProductTile",
  mixins: [saleTagMixin, tagableMixin],

  inject: { uid: { default: "" } },

  props: {
    product: { type: Object, required: true },
    selectedVariant: { type: Object, required: true },
    position: { type: [Number, String], required: true },
    initialChecked: { type: Boolean, default: true },
    selectionConfirmed: { type: Boolean, default: false },
  },

  data() {
    return {
      isChecked: this.initialChecked,
    };
  },

  computed: {
    metafields() {
      return {
        ...this.selectedVariant.metafields,
        ...this.product?.metafields,
      };
    },
    tags() {
      return this.product?.tags ?? [];
    },
    listname() {
      return this.product?.source
        ? `${this.product.source} - ${this.uid}`
        : this.uid;
    },
    productImages() {
      const variantImages = this.selectedVariant?.images ?? [];
      return variantImages.length > 0
        ? variantImages
        : this.product?.mediaImages ?? [];
    },
    imagesPresent() {
      return this.productImages.length > 0;
    },
    productImagesProps() {
      return {
        mainImage:
          this.selectedVariant.image ??
          this.product.featuredImage ??
          this.product.images?.[0] ??
          {},
        maxDiscount: this.maxDiscount,
        priorityCampaign: this.priorityCampaign,
        selectedVariant: this.selectedVariant,
        product: this.product,
        metafields: this.metafields,
        position: this.position,
        listname: this.listname,
        target: "_blank",
      };
    },
    productPriceProps() {
      return {
        selectedVariant: this.selectedVariant,
        product: this.product,
      };
    },
    ratingProps() {
      return {
        handle: this.product.handle,
        reviewsAverage: this.metafields.reviews_average,
        reviewsCount: this.metafields.reviews_count,
        selectedVariantId: this.selectedVariant.id,
      };
    },
    title() {
      return `${this.product.title} ${getVariantTitle({
        isMultiVariant: this.isMultiVariant,
        variant: this.selectedVariant,
      })}`;
    },
    selectedOptions() {
      const selectedOptions = this.selectedVariant?.selectedOptions ?? {};
      return Object.entries(selectedOptions).reduce(
        (acc, [k, v]) => ({
          ...acc,
          [slugify(camelize(k, true, " "))]: v.value ?? v,
        }),
        {}
      );
    },
    hasRating() {
      return Number(this.ratingProps.reviewsAverage ?? 0) > 0;
    },
    hasMoreVariants() {
      return (Number(this.product?.variants?.length) || 0) > 1;
    },
    selectOptionsLabel() {
      if (!this.selectionConfirmed) return "select options";
      const t = getVariantTitle(
        {
          isMultiVariant: this.isMultiVariant,
          variant: this.selectedVariant,
        },
        false
      );
      const text = String(t ?? "").trim();
      if (!text) return "select options";
      if (text.toLowerCase() === "default title") return "select options";
      return text;
    },
  },

  watch: {
    initialChecked(newVal, oldVal) {
      if (this.isChecked === oldVal) {
        this.isChecked = newVal;
      }
    },
  },

  created() {
    const saleTitle = this.metafields?.product_campaign_badge_title;
    const saleColor = this.metafields?.product_campaign_badge_color;

    this.getSaleTag({
      tags: this.tags,
      campaign: { saleTitle, saleColor },
      price: this.selectedVariant.price,
      compareAtPrice: this.selectedVariant.compareAtPrice,
    });
  },

  methods: {
    ...mapActions("recommendations", ["trackBeacon"]),
    ...mapActions("productsSearch", ["trackSearchClickthrough"]),

    async handleClick(event) {
      const { source, id } = this.product;

      if (source?.includes("searchspring-")) {
        const [, tag] = this.product.source.split("searchspring-");
        await this.trackBeacon({ type: "click", tag, productId: id });
      } else {
        // Search-module beacon clickthrough (PLP search results)
        await this.trackSearchClickthrough({ productId: id });
      }

      const anchor = event?.target?.closest?.("a");
      if (anchor && this.$searchspring?.trackProductClick) {
        this.$searchspring.trackProductClick(
          anchor,
          this.product.intellisuggestData,
          this.product.intellisuggestSignature
        );
      }
    },

    handleCheckboxClick(event) {
      // If currently checked, we're unchecking - just uncheck and return
      if (this.isChecked) {
        this.isChecked = false;
        this.$emit("checked", this.isChecked);
        return;
      }

      // If currently unchecked, we're checking
      // If product has multiple variants, just open the quickview (don't check yet)
      if (this.hasMoreVariants) {
        this.openBundleQuickview(event);
        return;
      }

      // For single variant products, check the checkbox immediately
      this.isChecked = true;
      this.$emit("checked", this.isChecked);
    },

    openBundleQuickview(event) {
      this.$store.dispatch("app/openProductQuickview", {
        variantId: this.selectedVariant.id,
        handle: this.product.handle,
        topPos: `${event.pageY - QUICKVIEW_VERTICAL_OFFSET}px`,
        context: {
          source: "bundle",
          productId: this.product.id,
        },
      });
    },
  },
};
